import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Users, Globe, Award, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import './who-we-are-premium.css';


export const WhoWeAre = () => {
  return (
    <div data-testid="who-we-are-page" className="bg-white text-stone-900">
      {/* Hero */}
      <section className="relative overflow-hidden py-24 md:py-36 text-white">
        {/* Hero image from public folder */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/assets/whatwedo.png')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-950/90 via-slate-900/75 to-black/70" />

        {/* subtle premium glow */}
        <div className="absolute -top-24 -left-24 w-[320px] h-[320px] rounded-full bg-amber-500/20 blur-3xl" />
        <div className="absolute -bottom-24 -right-16 w-[280px] h-[280px] rounded-full bg-blue-400/20 blur-3xl" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-sm font-medium backdrop-blur-sm mb-6">
              <Sparkles className="w-4 h-4 text-amber-300" />
              About GLGIF
            </span>

            <h1 className="font-heading text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Who We Are
            </h1>

            <p className="text-lg md:text-xl text-stone-200 max-w-2xl leading-relaxed">
              GLGIF is  humanity-centered nonprofit organization dedicated
              to protecting the vulnerable and promoting dignity for all.
            </p>

            <div className="mt-8">
              <Button
                asChild
                className="bg-amber-600 hover:bg-amber-700 text-white rounded-full px-8 py-6 text-base shadow-xl"
              >
                <Link to="/get-involved#partner" data-testid="hero-partner-btn">
                  Partner With Us <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
            <div>
              <p className="font-accent text-xl text-amber-700 mb-4">Our Foundation</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 mb-6 leading-tight">
                Built for Dignity, Protection, Opportunity
              </h2>

              <div className="space-y-6 text-stone-600 leading-relaxed text-[1.02rem]">
                <p>
                  GoldenLife Global Impact Foundation (GLGIF) is a humanitarian and human-rights
                  driven organization committed to restoring hope and improving the lives of
                  orphans, vulnerable persons, widows, displaced persons, and underserved communities.
                </p>
                <p>
                  We work with compassion, accountability, and inclusion—serving people regardless
                  of background, race, religion, or circumstance. Our approach combines immediate
                  humanitarian relief with long-term empowerment programs that create lasting change.
                </p>
                <p>
                  Founded in 2020 as Goldenlife Builders International by a group of passionate
                  teenagers with nothing but faith, love, and enthusiasm, we have grown into a
                  movement that has touched thousands of lives across Nigeria.
                </p>
              </div>

              <div className="mt-9">
                <Button
                  asChild
                  className="bg-amber-700 hover:bg-amber-800 text-white rounded-full px-8 py-5"
                >
                  <Link to="/about/mission-vision" data-testid="learn-mission-btn">
                    Our Mission & Vision <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </div>

            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl border border-stone-200/70">
                <img
                  src="/assets/about-main.png"
                  alt="GLGIF Team"
                  className="w-full h-[460px] object-cover"
                />
              </div>

              <div className="absolute -bottom-6 -right-6 bg-gradient-to-br from-amber-700 to-amber-900 text-white p-6 rounded-2xl shadow-xl hidden md:block">
                <p className="font-heading text-2xl leading-none mb-1">Serving Since</p>
                <p className="font-heading text-5xl font-bold leading-none">2020</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Attributes */}
      <section className="py-20 bg-gradient-to-b from-stone-50 to-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="font-accent text-xl text-amber-700 mb-3">What Defines Us</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900">
              Our Core Identity
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-7">
            <Card className="rounded-2xl border-stone-200/70 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-8 h-8 text-amber-700" />
                </div>
                <h3 className="font-heading text-xl font-bold text-stone-900 mb-2">Faith-Driven</h3>
                <p className="text-stone-600 text-sm">Guided by love for God and humanity in all we do.</p>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-stone-200/70 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-blue-700" />
                </div>
                <h3 className="font-heading text-xl font-bold text-stone-900 mb-2">Community-Centered</h3>
                <p className="text-stone-600 text-sm">Working within and for the communities we serve.</p>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-stone-200/70 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Globe className="w-8 h-8 text-green-700" />
                </div>
                <h3 className="font-heading text-xl font-bold text-stone-900 mb-2">Inclusive</h3>
                <p className="text-stone-600 text-sm">Serving all regardless of background or circumstance.</p>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-stone-200/70 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-purple-700" />
                </div>
                <h3 className="font-heading text-xl font-bold text-stone-900 mb-2">Accountable</h3>
                <p className="text-stone-600 text-sm">Transparent and responsible in all our operations.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Origin Story */}
      {/* <section className="py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="font-accent text-xl text-amber-700 mb-4">Our Story</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900">From 2020 to Today</h2>
          </div>

          <div className="rounded-2xl border border-stone-200 bg-white p-7 md:p-10 shadow-sm">
            <p className="text-xl italic text-stone-700 border-l-4 border-amber-700 pl-6 mb-8 leading-relaxed">
              "There are certain moments in life that leave a permanent mark on your heart.
For me, one of those moments was seeing children living on the streets with no place to call home.
"
            </p>

            <div className="space-y-6 text-stone-600 text-[1.02rem] leading-relaxed">
              <p>
              I remember walking through different communities and seeing young children sleeping on bare ground at night. Some sheltered themselves beneath torn plastic sheets and worn-out cardboard, using whatever they could find to protect themselves from the cold. Their clothes were dirty, their bodies carried bruises and untreated wounds, and their faces reflected a level of pain that no child should ever have to endure.

What affected me most was not simply their physical condition—it was the hopelessness in their eyes.

Many of these children had been forced into a life they never chose. Some were already being exposed to harmful influences, dangerous environments, and destructive habits simply because survival had become their daily reality. They were not bad children. They were vulnerable children trying to survive circumstances beyond their control.

I remember asking myself a simple but life-changing question:

*What if someone showed them genuine love? What if someone stepped in before it was too late? What if someone reminded them that they mattered?*

Those questions stayed with me long after I had left those streets.
The faces of those children stayed with me.
Their struggles stayed with me.
Their stories stayed with me.

And over time, those memories became one of the inspirations behind the vision that would eventually become GLGIF.

They helped shape a commitment to stand with vulnerable children, support struggling families, create opportunities for those who have been overlooked, and bring hope to people facing some of life's most difficult circumstances.

Today, whenever we carry out an outreach program, support a child, provide humanitarian assistance, advocate for education, or respond to the needs of a community, I am reminded of those moments.

I still see those faces.
They remind us why our work matters.
They remind us that behind every statistic is a human life.

Behind every challenge is a child with dreams.
Behind every struggle is someone deserving of dignity, compassion, and opportunity.


              </p>
              <p>
             At GLGIF, we believe that every child deserves safety, every family deserves hope, and every individual deserves the opportunity to build a better future.
This belief continues to drive our mission every day.

What began as a moment of compassion has grown into a lifelong commitment to transforming lives, restoring hope, and creating lasting impact in communities.
Because no child should ever be forgotten.
And no life should ever be considered beyond hope.
              </p>
            </div>
          </div>

          <div className="mt-12 text-center">
            <Button
              asChild
              variant="outline"
              className="border-2 border-amber-700 text-amber-700 hover:bg-amber-50 rounded-full px-8 py-5"
            >
              <Link to="/about/team" data-testid="meet-team-btn">
                Meet Our Team <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section> */}
    </div>
  );
};

export default WhoWeAre;
